<template>
  <PForm
    ref="pForm"
    :show-header="false"
    :primary-cta="formState?.edit ? updateButton : {}"
    :secondary-cta="{}"
    :style="{ height: 'auto !important' }"
    body-class="q-px-2x-md q-pt-xl overflow-hidden"
    @submit.prevent="onSubmit"
  >
    <div class="bg-primary-30 q-py-sm q-px-xs rounded-borders q-mb-xl">
      <div class="row items-center">
        <InfoIcon class="icon q-mr-xs" />
        <div class="col-8 text-sm text-weight-medium">
          <span
            v-if="
              user.consent?.terms_and_conditions_privacy?.status_cd == 'pending'
            "
            >Do you want to send consent message related to
          </span>
          <span v-else>Has {{ user?.basicInfo?.name }} given consent to </span>
          <span
            class="text-primary cursor-pointer"
            @click="
              () => openTncPrivacy('https://poshn.co/terms-conditions.html')
            "
            >T&C</span
          >,
          <span
            class="text-primary cursor-pointer"
            @click="
              () => openTncPrivacy('https://poshn.co/privacy-policy.html')
            "
            >Privacy policy</span
          >, and
          <span
            v-if="
              user.consent?.terms_and_conditions_privacy?.status_cd == 'pending'
            "
            >WhatsApp communication to {{ user?.basicInfo?.name }}</span
          >
          <span v-else>WhatsApp communication</span>
        </div>
        <PBtn
          v-if="
            user.consent?.terms_and_conditions_privacy?.status_cd == 'pending'
          "
          class="q-ml-auto"
          flat
          rounded
          label="Send now"
          dense
          @click="handleSendConsentDialog"
        />
        <PBadge
          v-else
          :label="computedConsentBadgeLabel"
          :type="user.consent?.terms_and_conditions_privacy?.status_cd"
          class="text-capitalize q-ml-auto"
        />
      </div>
    </div>
    <div class="col-12">
      <span class="text-md text-weight-medium text-neutral"> Business </span>
      <q-separator class="q-mb-sm" />
    </div>

    <div class="row q-col-gutter-x-md">
      <div class="col-6">
        <PInput
          v-model="formData.pan"
          :label="'PAN'"
          help="10-character PAN Number"
          :placeholder="'Enter PAN'"
          :validations="{ type: 'pan', length: 10 }"
          required
          :disable="computedPanDisable"
          class="q-pa-none"
          :loading="panLoading"
          maxlength="10"
          :error="panApiFailed"
          :error-message="computedPanErrorMessage"
          @update:model-value="handlePanUpdate"
        />
        <PBtn
          label="Get Details"
          flat
          text
          class="q-pa-none"
          :class="{
            'q-mt-md': computedPanErrorMessage,
          }"
          :disable="isPanDetailsDisabled"
          @click="fetchPanDetail"
        />
      </div>
      <div class="col-6">
        <PSelectInput
          v-model.trim="formData.gstin"
          label="GSTIN"
          help="15-digit GSTIN number"
          :placeholder="
            computedGstUnregistered ? 'Unregistered' : 'Enter GSTIN number'
          "
          required
          maxlength="15"
          class="q-pa-none"
          :loading="businessDetailsLoading"
          :rules="[(val) => validateGstin(val)]"
          :disable="computedGstDisabled"
          :error="gstValidationError"
          :error-message="computedGstErrorMessage"
          :options="unref(gstOptionsFilter.entities)"
          option-label="gstin"
          option-value="gstin"
          input-debounce="300"
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          :options-loading="unref(gstOptionsFilter.loading)"
          new-value-mode="add-unique"
          @new-value="updateManualGstInput"
          @virtual-scroll="gstOptionsFilter.onScroll"
          @filter="gstOptionsFilter.onFilter"
          @filter-abort="gstOptionsFilter.onFilterAbort"
          @update:model-value="gstinComputed"
        >
          <template #actionComponent>
            <PCheckbox
              v-model="computedGstUnregistered"
              label="Unregistered"
              left-label
              :disable="computedDisableGstcheckbox"
              @update:model-value="onUnregisteredToggle"
            />
          </template>
        </PSelectInput>
        <PBtn
          label="Get Details"
          flat
          text
          class="q-pa-none"
          :class="{
            'q-mt-xl': computedGstErrorMessage,
          }"
          :disable="computedDisableGetDetailsButton"
          @click="fetchBusinessDetailsFromGstin"
        />
      </div>
    </div>
    <div class="row q-mb-sm">
      <div class="col-12 q-mt-lg">
        <PInput
          v-model.trim="formData.shop_name"
          :label="
            'Registered ' +
            (formData.business_type?.display_label ?? 'Business') +
            ' Name'
          "
          :placeholder="
            'Registered ' +
            (formData.business_type?.display_label ?? 'business') +
            ' name'
          "
          required
          debounce="1200"
          :rules="[validateShopName]"
          :error="isShopNameNotValid"
          :error-message="shopNameErrorMessage"
          :disable="computedShopNameDisabled"
          @update:model-value="handleShopNameUpdate"
        />
      </div>
    </div>
    <div
      class="q-col-gutter-x-md q-col-gutter-y-xs"
      :class="formState?.edit ? 'column reverse' : 'column'"
    >
      <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
        <div class="col-6">
          <PSelectInput
            v-model.trim="computedBusinessType"
            label="Business Type"
            placeholder="Select business type"
            required
            :options="unref(businessTypesFilter?.entities)"
            :options-loading="unref(businessTypesFilter?.loading)"
            option-label="name"
            option-value="code"
            :option-disable="
              (opt) =>
                Object(opt) === opt
                  ? opt.code === 'PLC' && computedGstUnregistered
                  : true
            "
            emit-value
            map-options
            :disable="computedBusinessTypeDisabled"
            @filter="businessTypesFilter?.onFilter"
            @filter-abort="businessTypesFilter?.onFilterAbort"
          />
        </div>
        <div class="col-6">
          <PInput
            v-model.trim.number="formData.number_of_director"
            label="Number of Director(s)"
            placeholder="Enter number of director"
            required
            :validations="{
              gte: formData.business_type?.min_directors_count,
              lte: 15,
            }"
            :disable="isDirectorInputDisabled"
            type="number"
            @update:model-value="onUpdateDirectorsCount"
          />
        </div>
      </div>

      <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
        <div class="col-4">
          <PSelectInput
            v-model.trim="formData.gstin_status"
            label="GSTIN Status"
            placeholder="Select status"
            new-value-mode="add"
            required
            :disable="!gstApiFailed"
            :options="gstinStatusOptions"
            option-label="label"
            option-value="value"
          />
        </div>
        <div class="col-4">
          <div class="row items-center">
            <PInput
              v-model.trim="computedGstFilingStatus"
              label="GSTR1 Filing Status"
              :placeholder="
                !gstApiFailed ? 'Enter filing status' : 'Filed (DD/MM/YYYY)'
              "
              required
              :disable="!gstApiFailed"
              class="col-9"
              :rules="[validateGstFilingStatus]"
            />
            <PBtn
              :to="`/users/${user?._id}/${formData.gstin}/gst-filing-report`"
              class="q-mt-xs q-ml-xs"
              target="_blank"
              dense
              flat
              rounded
              :disable="
                !formData.gstin ||
                !formData.shop_name ||
                computedGstUnregistered
              "
            >
              <ViewIcon class="icon--md" />
              <PTooltip> View Report </PTooltip>
            </PBtn>
          </div>
        </div>
        <div class="col-4">
          <PSelectInput
            v-model.trim="formData.filing_frequency"
            label="Filing Frequency"
            :label-class="'text-capitalize'"
            class="text-capitalize"
            placeholder="Select filing frequency"
            required
            :disable="computedGstUnregistered"
            :options="[
              { label: 'Regular', value: 'regular' },
              { label: 'Not Regular', value: 'not regular' },
            ]"
          />
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-x-xl q-col-gutter-y-xs q-my-xxs">
      <div class="col-6">
        <PVDateInput
          v-model="formData.registration_date"
          label="Date of Registration"
          placeholder="DD-MM-YYYY"
          :disable="!gstApiFailed"
        />
      </div>
      <div class="col-6">
        <PVDateInput
          v-model="formData.incorporation_date"
          label="Date of Incorporation"
          placeholder="DD-MM-YYYY"
          :required="user?.basicInfo?.distribution_type !== 'retailer'"
          :max-date="currentDate"
        />
      </div>

      <div class="col-6 q-mt-lg">
        <div class="q-mb-xl">
          Aggregate TurnOver Range
          <span v-if="!computedGstUnregistered" class="text-negative">*</span>
        </div>
        <div class="row">
          <div class="col-5">
            <PInput
              v-model.number="computedMinimumAggregateTurnover"
              placeholder="Min"
              min="0"
              :validations="{ gte: 0 }"
              :required="
                gstApiFailed || computedMaximumAggregateTurnover != null
              "
              class="prepend-bg"
              :disable="
                (!computedGstUnregistered && !gstApiFailed) ||
                (formState?.edit &&
                  ['approved', 'rejected'].includes(user.application_status) &&
                  isBusinessStepCompleted)
              "
              type="number"
              @keydown.space.prevent
            >
              <template #prepend>
                <span class="text-sm">₹</span>
              </template>
            </PInput>
            <PTooltip>{{ computedMinimumAggregateTurnover }}</PTooltip>
          </div>

          <div class="col-2"></div>
          <div class="col-5">
            <PInput
              v-model.number="computedMaximumAggregateTurnover"
              placeholder="Max"
              min="0"
              :validations="{ gte: computedMinimumAggregateTurnover || 0 }"
              :required="
                gstApiFailed || computedMinimumAggregateTurnover != null
              "
              class="prepend-bg"
              :disable="isMinAggregateTurnoverDisabled"
              type="number"
              @keydown.space.prevent
            >
              <template #prepend>
                <span class="text-sm">₹</span>
              </template>
            </PInput>
            <PTooltip>{{ computedMaximumAggregateTurnover }}</PTooltip>
          </div>
        </div>
      </div>

      <div class="col-3 q-mt-lg">
        <PInput
          v-model.trim="formData.fssai"
          label="FSSAI"
          help="14-digit FSSAI number"
          placeholder="Enter FSSAI number"
          :validations="{ length: 14, type: 'integer' }"
          maxlength="14"
        />
      </div>

      <div class="col-3 q-mt-lg">
        <PVDateInput
          v-model="formData.fssai_expiration_date"
          label="FSSAI Expiry Date"
          help="<br>"
          placeholder="DD-MM-YYYY"
          :required="isFssaiExpirationDateRequired"
        />
      </div>
    </div>

    <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
      <div class="col-6">
        <div class="row">
          <div class="col-7 text-neutral text-sm">
            <div>MSME Registered?</div>
            <div>
              <span>No</span>
              <q-toggle v-model="formData.is_msme_registered" size="md" />
              <span>Yes</span>
            </div>
          </div>
          <div
            v-if="formData.is_msme_registered"
            class="col-5 text-neutral text-sm"
          >
            <div>Trade MSME?</div>
            <div>
              <span>No</span>
              <q-toggle v-model="formData.is_trade_msme" size="md" />
              <span>Yes</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="formData.is_msme_registered" class="col-3">
        <PInput
          v-model.trim="formData.udyam_aadhar"
          :rules="[(val) => validateUdyamAadhaar(val)]"
          label="Udyam Aadhaar"
          help="UDYAM-XX-00-0000000"
          placeholder="Enter Udyam Aadhaar"
          maxlength="19"
          :required="formData.is_msme_registered"
          :validations="{ length: 19 }"
          @update:model-value="handleUdyamUpdate"
        />
      </div>
      <div v-if="formData.is_msme_registered" class="col-3">
        <PSelectInput
          v-model.trim="formData.msme_category"
          label="MSME Category"
          label-class="text-capitalize"
          class="text-capitalize"
          placeholder="Select MSME Category"
          :loading="msmeLoading"
          :disable="computedMsmeCategoryDisabled"
          :options="[
            { label: 'Micro', value: 'micro' },
            { label: 'Small', value: 'small' },
            { label: 'Medium', value: 'medium' },
          ]"
        />
      </div>
    </div>

    <div v-if="!formState?.edit" class="q-mt-lg">
      <div class="col-12">
        <span class="text-md text-weight-medium text-neutral">
          Registered Business Address
        </span>
        <q-separator class="q-mb-sm" />
      </div>

      <div class="row q-col-gutter-x-xl q-col-gutter-y-sm">
        <div class="col-4">
          <PInput
            v-model.trim="formData.pincode"
            label="Pincode"
            placeholder="Enter pincode"
            required
            :validations="{ length: 6, type: 'integer' }"
            maxlength="6"
            :disable="
              isDisabledBasedOnValue.pincode &&
              !computedGstUnregistered &&
              !gstApiFailed
            "
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.city"
            :options="unref(cityFilter.entities)"
            :loading="citiesLoading"
            label="City"
            hint="Press enter to add new city"
            option-label="name"
            option-value="name"
            placeholder="Enter city"
            required
            new-value-mode="add-unique"
            @new-value="onCreateCity"
            @filter="cityFilter.onFilter"
            @filter-abort="cityFilter.onFilterAbort"
          />
        </div>
        <div class="col-4">
          <PSelectInput
            v-model="formData.state_cd"
            :options="unref(stateFilter.entities)"
            label="State"
            option-label="name"
            option-value="state_code"
            placeholder="Select state"
            required
            :disable="
              isDisabledBasedOnValue.state &&
              !computedGstUnregistered &&
              !gstApiFailed
            "
            @filter="stateFilter.onFilter"
            @filter-abort="stateFilter.onFilterAbort"
          />
        </div>
      </div>
      <div class="col-12">
        <PInput
          v-model="computedAddress"
          :validations="{
            label: 'Address',
            maxLength: 100,
            required: true,
          }"
          label="Address Line 1"
          placeholder="Enter your detail address, e.g. place name, street, etc."
          type="textarea"
          autogrow
          required
          :disable="
            isDisabledBasedOnValue.address &&
            !computedGstUnregistered &&
            !gstApiFailed
          "
        />
      </div>
    </div>
    <div v-if="!(formData.business_type?.code === 'PLC')" class="q-mt-lg">
      <KycForm
        v-model:directors="computedDirectors"
        :is-completed="
          formState?.edit ? user.application_stage?.kyc?.is_completed : false
        "
        :is-gst-data="
          formData?.directorNames?.length &&
          !gstApiFailed &&
          !computedGstUnregistered
        "
      />
    </div>

    <div class="q-py-md">
      <span class="text-md text-weight-medium text-neutral">
        Other Reports
      </span>
      <q-separator class="q-mb-sm" />
      <section>
        <span class="text-xs text-weight-medium text-neutral q-mr-xxl">
          MCA Records
        </span>
        <template v-if="formData.mcaRecords">
          <span class="q-ml-xxl text-xs">Download </span>
          <PBtn
            :disable="!mcaPdfReport"
            color="primary"
            dense
            flat
            round
            target="_blank"
            @click="viewFile(mcaPdfReport)"
          >
            <PdfIcon class="icon" />
            <PTooltip>
              PDF <span v-if="!mcaPdfReport">not available</span></PTooltip
            >
          </PBtn>
          <PBtn
            color="primary"
            :disable="!mcaExcelReport"
            dense
            flat
            round
            @click="downloadFile(mcaExcelReport)"
          >
            <ExcelIcon class="icon--md" />
            <PTooltip>
              Excel <span v-if="!mcaExcelReport">not available</span></PTooltip
            >
          </PBtn>
        </template>
        <span v-else class="q-ml-xxl text-xs">Not Available </span>
      </section>
    </div>
  </PForm>
</template>

<script setup>
import ViewIcon from "assets/icons/view-detail.svg";
import InfoIcon from "assets/icons/info.svg";
import businessKycFormModel from "src/modules/user/models/businessKycForm";
import { useQuasar } from "quasar";
import { getGstDetails } from "src/api/organisations";
import { getPincodeDetails } from "src/api/address";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import ConsentInfoDialog from "./ConsentInfoDialog.vue";
import { capitalizePreservingSpecialChars } from "assets/scripts/utilities/helpers";
import { useBusinessTypes } from "src/composables/businessTypes";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import {
  postValidateBusinessName,
  postUdyamAadharDetails,
  postuploadUdyamDocument,
} from "src/api/user";
import { omitKeys } from "src/assets/scripts/utilities/helpers";
import {
  ORG_OMIT_KEYS,
  VALID_BUSINESS_CODES,
} from "../constants/userConstants";

import PdfIcon from "assets/icons/pdf.svg";
import ExcelIcon from "assets/icons/excel.svg";
import { notify } from "assets/scripts/utilities/api-clients";

const $q = useQuasar();

const { queryParams, hash } = useRouterUtilities();

const pForm = ref(null);
const isShopNameNotValid = ref(false);
const shopNameErrorMessage = ref("");
const keepShopNameFieldActive = ref(false);
const currentDate = new Date();

const msmeLoading = ref(false);
const isUdyamAutoFilled = ref(false);

const { selectBusinessType, getShopName, businessTypesFilter } =
  useBusinessTypes();

const { formState, user } = storeToRefs(useUserStore());
const { putUserBusinessDetails, toggleUserForm, putUserConsentInfo } =
  useUserStore();

const { organisation } = storeToRefs(useOrganisationStore());
const { postOrganisation, getUnregisteredOrg, setOrganisation } =
  useOrganisationStore();

const openTncPrivacy = (url) => {
  window.open(url, "_blank");
};

const computedConsentBadgeLabel = computed({
  get() {
    const status =
      unref(user)?.consent?.terms_and_conditions_privacy?.status_cd;

    if (status === "awaiting_response") return "Awaiting Response";

    return status;
  },
});

function onUnregisteredToggle() {
  const currentGstRegistered = formData.value.is_gst_registered;

  resetForm();

  formData.value.is_gst_registered = currentGstRegistered;
}

const isFssaiExpirationDateRequired = computed(() => {
  return Boolean(unref(formData).fssai);
});

const handleSendConsentDialog = () => {
  $q.dialog({
    component: ConsentInfoDialog,
    componentProps: {
      headerText: "Send Consent Message",
      labelText:
        "Consent message related to T&C, Privacy Policy and WhatsApp Communication will be sent to the following contact.",
      hintText: `${unref(user)?.basicInfo?.name} | ${
        unref(user)?.basicInfo?.phone
      }`,
      labelClass: "text-neutral-80 text-md",
      hintClass: "text-neutral text-weight-medium text-md",
      submitFunction: () =>
        putUserConsentInfo({ status_cd: "awaiting_response" }),
    },
  });
};

const isBusinessStepCompleted = computed(() => {
  return unref(user).application_stage?.business?.is_completed;
});

const derivedRole = computed(() => {
  if (unref(hash) === "#customers") return "buyer";

  if (unref(hash) === "#vendors") return "seller";

  return "";
});

const directorModel = ref({
  name: "",
  phone: "",
  pan: "",
  aadhar: "",
  date_of_birth: "",
  credit_score: null,
  is_primary: false,
});

const isDirectorMatching = ref(true);

const getDirectorsList = (directorsName) => {
  if (!Array.isArray(directorsName) || directorsName.length === 0) {
    isDirectorMatching.value = false;

    return [{ ...directorModel.value }];
  }

  const initialDirectorsList = [];
  const enteredContactName = unref(user).basicInfo?.name || "";
  const enteredPhoneNumber = unref(user).basicInfo?.phone || "";
  const pattern = enteredContactName
    ? new RegExp(enteredContactName, "i")
    : null;

  const primaryDirector = pattern
    ? directorsName.find(
        (name) => typeof name === "string" && pattern.test(name),
      )
    : null;

  if (primaryDirector) {
    initialDirectorsList.push({
      ...unref(directorModel),
      name: primaryDirector,
      phone: enteredPhoneNumber,
    });
  } else {
    isDirectorMatching.value = false;
  }

  return directorsName.reduce((directorsList, name) => {
    if (typeof name === "string" && name !== primaryDirector) {
      directorsList.push({
        ...unref(directorModel),
        name: useChangeCase(name.trim(), "capitalCase")?.value || "",
      });
    }

    return directorsList;
  }, initialDirectorsList);
};

const computedEntity = computed(() => unref(user).businessDetails);

const { formData, isLoading, submitForm, resetForm } = useForm({
  formModel: businessKycFormModel(),
  entity: computedEntity,
  formRef: pForm,
  isForm: formState,
  put: putUserBusinessDetails,
  toggleForm: toggleUserForm,
  editMode: !!unref(user).businessDetails?.shop_name,
});

const {
  fetchPanDetail,
  gstOptionsFilter,
  panApiFailed,
  panLoading,
  computedPanErrorMessage,
  isPanDetailsDisabled,
  computedGstUnregistered,
  handlePanUpdate,
} = usePanUtilities({
  formData,
  formState,
  isBusinessStepCompleted,
});

const isDisabledBasedOnValue = reactive({});

const diableBasedOnValue = (formData) => {
  for (const key of Object.keys(formData)) {
    isDisabledBasedOnValue[key] = !!formData[key];
  }
};

const mapOrganisationToFormData = async () => {
  if (!unref(organisation) || unref(isBusinessStepCompleted)) return;

  const org = unref(organisation);
  const isGstRegistered = org.is_gst_registered;
  const basicDetails = {
    shop_name: org.name,
    is_gst_registered: false,
    pan: org.pan,
  };

  if (!isGstRegistered) {
    formData.value = { ...unref(formData), ...basicDetails };

    return;
  }

  const businessType = await selectBusinessType(org.constitutionOfBusiness);
  const shopNameKey = getShopName(businessType?.code);

  if (org.gstinList && org.gstinList.length === 1) {
    formData.value.shop_name = capitalizePreservingSpecialChars(
      org[shopNameKey] || org.name,
    );
  }

  let gstinValue = "";

  if (unref(isBusinessStepCompleted)) {
    gstinValue = org?.gstin || org?.gstin_business;
  } else if (org?.gstinList && org.gstinList.length === 1) {
    gstinValue = org?.gstinList[0]?.gstin || "";
  }

  formData.value = {
    ...unref(formData),
    pan: org.pan,
    is_gst_registered: isGstRegistered,
    gstin: gstinValue,
    legal_business_name: org.legalNameOfBusiness,
    business_type: businessType,
    registration_date: org.registrationDate,
    gstin_status: org.gstinStatus,
    latest_gst_filed: org.filingStatus,
    gst_filing_status_count: org.filingStatusCount,
    total_gross_income:
      org.grossTotalIncome === "NA" ? 0 : org.grossTotalIncome,
    annual_aggregate_turnover: org.annualAggregateTurnOver,
    aggregate_turnover_range: org.aggregateTurnOverRange,
    directors: getDirectorsList(org?.directorNames),
    address: org.principalPlace?.addressLine,
    city: org.principalPlace?.city,
    state_cd: org.principalPlace?.state_cd,
    country: org.principalPlace?.country,
    country_cd: org.principalPlace?.country_cd,
    pincode: org.principalPlace?.pincode,
    number_of_director: org.directorNames?.length || 1,
  };

  diableBasedOnValue(unref(formData));
};

const gstValidationError = computed(() => {
  if (!isBusinessStepCompleted.value) {
    const gstin = formData.value.gstin || "";
    const pan = formData.value.pan || "";

    if (gstin.length === 15 && pan.length === 10) {
      return gstin.substring(2, 12) !== pan;
    }
  }

  return false;
});

const validateGstFilingStatus = (value) => {
  const trimmed = value?.trim();

  if (!trimmed) return "Filing status is required";

  const pattern = /^Filed\s\((\d{2})\/(\d{2})\/(\d{4})\)$/;

  return pattern.test(trimmed)
    ? true
    : "Format must be exactly 'Filed (DD/MM/YYYY)' with '/' separators.";
};

const computedGstErrorMessage = computed(() => {
  if (!isBusinessStepCompleted.value && gstValidationError.value)
    return `The GSTIN entered does not match the PAN  ${unref(formData)?.pan} of the organisation.`;
  else return "";
});

const computedPanDisable = computed(() => {
  return (
    (unref(formState).edit && unref(isBusinessStepCompleted)) ||
    unref(isBusinessStepCompleted) ||
    Boolean(unref(queryParams)?.add)
  );
});

const computedGstDisabled = computed(() => {
  return (
    computedGstUnregistered.value ||
    (unref(formState).edit && unref(isBusinessStepCompleted)) ||
    isBusinessStepCompleted.value
  );
});

const computedShopNameDisabled = computed(() => {
  const isGstApiSuccessful = !gstApiFailed.value;

  return (
    (!keepShopNameFieldActive.value &&
      unref(formData).is_gst_registered &&
      isGstApiSuccessful) ||
    isBusinessStepCompleted.value
  );
});

const isDirectorInputDisabled = computed(() => {
  const isGstApiSuccessful = !gstApiFailed.value;
  const isGstRegistered = !computedGstUnregistered.value;
  const isEditingCompletedBusiness =
    formState.value?.edit && isBusinessStepCompleted.value;

  return (
    (isGstRegistered && isGstApiSuccessful) ||
    isDirectorCountNotRequired.value ||
    isEditingCompletedBusiness
  );
});

const isMinAggregateTurnoverDisabled = computed(() => {
  const isGstApiSuccessful = !gstApiFailed.value;
  const isGstRegistered = !computedGstUnregistered.value;
  const isGstDataAvailable = isGstRegistered && isGstApiSuccessful;

  const isEditingApprovedOrRejected =
    formState.value?.edit &&
    ["approved", "rejected"].includes(user.value.application_status) &&
    isBusinessStepCompleted.value;

  return isGstDataAvailable || isEditingApprovedOrRejected;
});

const gstinStatusOptions = [
  { label: "ACTIVE", value: "ACTIVE" },
  { label: "INACTIVE", value: "INACTIVE" },
];

const validateAndTriggerShopName = async () => {
  const validationResult = await validateShopName(unref(formData).shop_name);

  if (validationResult !== true) {
    isShopNameNotValid.value = true;

    shopNameErrorMessage.value = validationResult;

    keepShopNameFieldActive.value = true;
  }
};

const addOrganisation = async () => {
  let orgData = {};
  const {
    _id,
    pan,
    shop_name,
    is_gst_registered,
    gstinList,
    gstin,
    internal_number,
  } = formData.value;

  if (!_id && !isBusinessStepCompleted.value) {
    const payload = {
      pan,
      name: shop_name,
      is_gst_registered,
    };

    if (is_gst_registered) {
      payload.gstinList = gstinList;
    }

    const response = await postOrganisation(payload, {
      params: { org: true },
    });

    orgData = response?.data?.data;
  }

  const cleanedFormData = omitKeys(
    {
      ...unref(formData),
      role: derivedRole.value,
      org_gst: gstin,
      organisations: {
        _id: orgData?._id || _id,
        internal_number: orgData?.internal_number || internal_number,
        gstin,
      },
    },
    ORG_OMIT_KEYS,
  );

  formData.value = cleanedFormData;

  await submitForm(false);
};

const fetchBusinessDetailsFromGstin = async () => {
  if (gstValidationError.value || !validateGstin(unref(formData).gstin)) return;

  businessDetailsLoading.value = true;

  keepShopNameFieldActive.value = false;

  gstApiFailed.value = false;

  try {
    const data = await getGstDetails({
      gstin: unref(formData).gstin,
      user_id: unref(user)?._id,
    });

    const response = data?.data?.data;

    if (response?.gst_api_failed === true) {
      gstApiFailed.value = true;

      formData.value.gst_filing_status_count = 1;

      formData.value = {
        ...unref(formData),
        ...response,
      };

      notify({
        message:
          "Couldn’t fetch details automatically. Please update manually.",
        type: "negative",
      });

      return;
    }

    if (!response) throw new Error("Invalid response from GST API");

    const businessType = await selectBusinessType(
      response.constitutionOfBusiness,
    );
    const shopNameKey = getShopName(businessType?.code);

    formData.value = {
      ...unref(formData),
      ...response,
      shop_name: response?._id
        ? response?.name
        : capitalizePreservingSpecialChars(response[shopNameKey]) || "",
      business_type: businessType,
      total_gross_income:
        response.grossTotalIncome === "NA" ? 0 : response.grossTotalIncome,
      directors: getDirectorsList(response.directorNames),
      legal_business_name: response.legalNameOfBusiness,
      registration_date: response.registrationDate,
      gstin_status: response.gstinStatus,
      latest_gst_filed: response.filingStatus,
      gst_filing_status_count: response.filingStatusCount,
      annual_aggregate_turnover: response.annualAggregateTurnOver,
      aggregate_turnover_range: response.aggregateTurnOverRange,
      address: response.principalPlace?.addressLine,
      city: response.principalPlace?.city,
      state_cd: response.principalPlace?.state_cd,
      country: response.principalPlace?.country,
      country_cd: response.principalPlace?.country_cd,
      pincode: response.principalPlace?.pincode,
      number_of_director: response.directorNames?.length || 1,
    };

    await onUpdatePincode();

    if (response.principalPlace?.state_cd) {
      await initCityFilter(response.principalPlace.state_cd);
    }

    diableBasedOnValue(unref(formData));

    await validateAndTriggerShopName();
  } catch (error) {
    console.error("Failed to fetch business details:", error);

    gstApiFailed.value = true;
  } finally {
    businessDetailsLoading.value = false;
  }
};

const validateUnregisteredShopName = async (shop_name) => {
  try {
    const response = await getUnregisteredOrg(shop_name);

    if (response === null) {
      if (formData.value._id) {
        formData.value._id = null;
      }

      if (formData.value.internal_number) {
        formData.value.internal_number = null;
      }

      setOrganisation(null);
    } else if (response._id) {
      formData.value._id = response._id;

      formData.value.internal_number = response.internal_number;

      formData.value.pan = response.pan;

      setOrganisation(response);
    }
  } catch (error) {
    console.error("Error fetching unregistered data:", error);
  }

  return true;
};

const validateRegisteredShopName = async (shop_name) => {
  const businessCode = unref(formData).business_type?.code;

  if (!VALID_BUSINESS_CODES.includes(businessCode)) {
    return true;
  }

  try {
    const payload = {
      shop_name: shop_name.toLowerCase(),
      role: derivedRole.value,
      gstin: unref(formData).gstin,
    };

    await postValidateBusinessName(payload);

    return true;
  } catch {
    return "Business with this name exists.";
  }
};

const validateShopName = async (shop_name) => {
  if (unref(isBusinessStepCompleted) || !shop_name) {
    return true;
  }

  if (!unref(formData).is_gst_registered) {
    return validateUnregisteredShopName(shop_name);
  }

  return validateRegisteredShopName(shop_name);
};

const handleShopNameUpdate = (name) => {
  shopNameErrorMessage.value = "";

  isShopNameNotValid.value = false;

  formData.value.shop_name = capitalizePreservingSpecialChars(name);
};

defineExpose({ pForm, gstValidationError, msmeLoading });

const gstinComputed = computed({
  get: () => formData.value.gstin,
  set: async (value) => {
    formData.value.gstin = value.gstin.toUpperCase();

    validateGstin(value);

    await fetchBusinessDetailsFromGstin();
  },
});

const computedDisableGstcheckbox = computed(() => {
  return (
    (unref(formState).edit && unref(isBusinessStepCompleted)) ||
    unref(isBusinessStepCompleted) ||
    Boolean(queryParams?.add)
  );
});

watch(
  [organisation, queryParams],
  () => {
    if (unref(queryParams)?.add) {
      nextTick(mapOrganisationToFormData);
    }
  },
  { immediate: true },
);

const manualGstFilingInput = ref("");

const computedGstFilingStatus = computed({
  get() {
    const formDataValue = unref(formData);
    const { latest_gst_filed } = formDataValue;
    const dateOfFiling = latest_gst_filed?.dateOfFiling || "";

    if (gstApiFailed.value) {
      return (
        manualGstFilingInput.value?.trim() ||
        [latest_gst_filed?.gstStatus, dateOfFiling]
          .filter(Boolean)
          .join(" ")
          .trim()
      );
    }

    if (formDataValue.gst_filing_status_count) {
      return formDataValue.gst_filing_status_count > 0
        ? `Filed (${formDataValue.latest_gst_filed?.dateOfFiling || ""})`
        : "Not Filed";
    }

    return "";
  },

  set(userInput) {
    manualGstFilingInput.value = userInput;

    if (!formData.value.latest_gst_filed) {
      formData.value.latest_gst_filed = {};
    }

    const filingData = formData.value.latest_gst_filed;

    if (!userInput?.trim()) {
      filingData.gstStatus = "";

      filingData.dateOfFiling = "";

      manualGstFilingInput.value = "";

      return;
    }

    const filingDate = userInput.match(/^((?:\w+(?:\s+\w+)*)?)\s*\(([^)]+)\)$/);

    if (filingDate) {
      const [, status, date] = filingDate;

      filingData.gstStatus = status.trim();

      filingData.dateOfFiling = date.trim();
    } else {
      filingData.gstStatus = "Filed";

      filingData.dateOfFiling = userInput.trim();
    }

    try {
      const [, month, year] = filingData.dateOfFiling.split("/").map(Number);

      const monthNames = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const prevMonthIndex = month - 2 >= 0 ? month - 2 : 11;

      filingData.monthOfFiling = monthNames[prevMonthIndex];

      const fyStart = month >= 4 ? year - 1 : year - 2;
      const fyEnd = fyStart + 1;

      filingData.filingYear = `${fyStart}-${fyEnd}`;

      filingData.methodOfFilling = "ONLINE";

      filingData.gstType = "GSTR1";
    } catch (err) {
      console.error("Error computing derived GST fields:", err);
    }
  },
});

const { states, stateFilter } = useStateFilter();
let { cities, cityFilter, citiesLoading, initCityFilter } = useCityFilter();

const isValidGstin = ref(false);
const validateGstin = (value) => {
  if (/^\d{2}[A-Z]{5}\d{4}[A-Z][0-9A-Z]{3}$/.test(value)) {
    isValidGstin.value = true;

    return true;
  }

  isValidGstin.value = false;

  return false;
};

const validateUdyamAadhaar = (value) =>
  !value || /^[A-Za-z0-9]{5}-[A-Za-z]{2}-\d{2}-\d{7}$/.test(value);

const setEnterpriseType = (enterpriseType) => {
  if (enterpriseType) {
    formData.value.msme_category = enterpriseType.toLowerCase();

    isUdyamAutoFilled.value = true;
  } else {
    isUdyamAutoFilled.value = false;
  }
};

const udyamApiFailed = ref(false);

const computedMsmeCategoryDisabled = computed(() => {
  return !udyamApiFailed.value;
});
const uploadUdyamDocumentHandler = async (pdfUrl, userId) => {
  const gstin = formData.value.gstin?.trim()?.toUpperCase();

  if (!pdfUrl) return;

  try {
    await postuploadUdyamDocument({
      pdfUrl,
      user_id: userId,
      is_gst_registered: formData.value.is_gst_registered,
      ...(gstin ? { gstin } : {}),
    });
  } catch (err) {
    console.error("Udyam document upload failed:", err);
  }
};

const fetchUdyamDetails = async (udyam) => {
  msmeLoading.value = true;

  udyamApiFailed.value = false;

  try {
    const res = await postUdyamAadharDetails({
      udyamNumber: udyam,
      shop_name: unref(formData).shop_name,
      user_id: unref(user)._id,
    });

    const data = res?.data?.data;

    setEnterpriseType(data?.enterpriseType);

    udyamApiFailed.value = false;

    await uploadUdyamDocumentHandler(data?.pdfUrl, unref(user)._id);
  } catch (error) {
    console.error(error);

    isUdyamAutoFilled.value = false;

    udyamApiFailed.value = true;
  } finally {
    msmeLoading.value = false;
  }
};

const handleUdyamUpdate = async (value) => {
  const formatted = value?.toUpperCase() || "";

  formData.value.udyam_aadhar = formatted;

  const isValid =
    validateUdyamAadhaar(formatted) === true && formatted.length === 19;

  if (!isValid) return;

  await fetchUdyamDetails(formatted);
};

const computedBusinessType = computed({
  get() {
    const businessTypes = unref(businessTypesFilter.value?.entities) || [];

    return businessTypes.find(
      (type) => type.code === unref(formData).business_type?.code,
    )?.name;
  },
  set(businessType) {
    const businessTypes = unref(businessTypesFilter.value?.entities) || [];
    const selectedType = businessTypes.find(
      (type) => type.code === businessType,
    );

    if (selectedType) {
      formData.value.business_type = selectedType;

      formData.value.number_of_director = selectedType.min_directors_count ?? 1;

      onUpdateDirectorsCount();
    }

    validateAndTriggerShopName();
  },
});

const computedBusinessTypeDisabled = computed(() => {
  const isEditMode = unref(formState)?.edit;
  const isBusinessTypeDisabled = unref(isDisabledBasedOnValue).business_type;
  const isGstRegistered = !computedGstUnregistered.value;
  const isGstApiSuccessful = !gstApiFailed.value;
  const isCompleted = unref(isBusinessStepCompleted);

  return (
    (isEditMode && isCompleted) ||
    (isBusinessTypeDisabled && isGstRegistered && isGstApiSuccessful)
  );
});

const isDirectorCountNotRequired = computed(() => {
  const selectedBusinessTypeCode = unref(formData).business_type?.code;

  if (!selectedBusinessTypeCode) return true;

  return (
    selectedBusinessTypeCode == "SP" ||
    selectedBusinessTypeCode == "OPC" ||
    selectedBusinessTypeCode == "PLC"
  );
});

const onUpdateDirectorsCount = () => {
  const count = unref(formData).number_of_director;

  if (!count || count < 1 || count > 15) return;

  let directorsList = [];

  for (let director = 0; director < count; director++) {
    directorsList.push({ ...unref(directorModel) });
  }

  unref(formData).directors = directorsList;

  if (!unref(formData)?.is_gst_registered && unref(formData).directors[0]) {
    unref(formData).directors[0].name = unref(user)?.basicInfo?.name;
  }
};

const computedMinimumAggregateTurnover = computed({
  get() {
    return unref(formData).aggregate_turnover_range?.minimum ?? null;
  },
  set(value) {
    unref(formData).aggregate_turnover_range.minimum = value;
  },
});

const computedMaximumAggregateTurnover = computed({
  get() {
    return unref(formData).aggregate_turnover_range?.maximum ?? null;
  },
  set(value) {
    unref(formData).aggregate_turnover_range.maximum = value;
  },
});

const computedDirectors = computed({
  get() {
    return unref(formData).directors?.sort(
      (a, b) => b.is_primary - a.is_primary,
    );
  },
});

const computedAddress = computed({
  get: () => {
    const address = Array.isArray(unref(formData).address)
      ? unref(formData).address[0]
      : unref(formData).address;

    return address;
  },
  set: (value) => {
    unref(formData).address = value;
  },
});

const businessDetailsLoading = ref(false);
const gstApiFailed = ref(false);

const onSubmit = async () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      text: `Are you sure you want to edit these details ?`,
      isFooterVisible: true,
      submittable: true,
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        try {
          await addOrganisation();
        } catch (error) {
          console.error(error);
        }
      },
    },
  });
};

const computedDisableGetDetailsButton = computed(() => {
  const gstin = formData.value.gstin;

  return (
    !gstin ||
    gstin.length !== 15 ||
    computedGstUnregistered.value ||
    isBusinessStepCompleted.value ||
    gstValidationError.value
  );
});

const updateManualGstInput = (gstin, done) => {
  done(gstin);

  formData.value.gstin = gstin.toUpperCase();
};

const isPincodeLoading = ref(false);
const onUpdatePincode = async () => {
  const enteredPincode = unref(formData).pincode;

  if (enteredPincode?.length == 6 && !Number.isNaN(Number(enteredPincode))) {
    isPincodeLoading.value = true;

    const response = await getPincodeDetails(enteredPincode);

    unref(formData)[`city`] = response?.data?.[0]?.PostOffice?.[0]?.District;

    unref(formData)[`state_cd`] = unref(states).find(
      (state) => state.name === response?.data?.[0]?.PostOffice?.[0]?.State,
    )?.state_code;

    unref(formData)[`country`] = response?.data?.[0]?.PostOffice?.[0]?.Country;

    isPincodeLoading.value = false;
  }
};

const onCreateCity = (city, done) => {
  done(city);

  unref(formData).city = city;

  cities.value.push({ name: city, _id: null });

  cityFilter = useFilter({
    entities: cities,
    entity: "city",
  });
};

watch(
  () => unref(formData).pincode,
  (pincodeValue) => {
    if (pincodeValue?.length == 6) {
      nextTick(onUpdatePincode);
    }
  },
);

watch(
  () => unref(formData).state_cd,
  (newState, previousState) => {
    if (previousState !== newState) {
      if (unref(gstApiFailed) || unref(computedGstUnregistered)) {
        if (previousState && newState) {
          unref(formData).city = "";
        }
      }

      initCityFilter(newState);
    }
  },
);

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const emit = defineEmits(["update:modelValue"]);

const computedFormData = computed({
  get: function () {
    return props.modelValue;
  },
  set: function (updatedFormData) {
    emit("update:modelValue", updatedFormData);
  },
});

const updateButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Update",
    type: "update",
    disable: isLoading.value || msmeLoading.value,
  };
});

watch(
  () => unref(formData).fssai,
  (fssaiValue) => {
    if (!fssaiValue?.trim()) {
      unref(formData).fssai_expiration_date = null;
    }
  },
);

watch(
  formData,
  (data) => {
    computedFormData.value = data;
  },
  {
    immediate: true,
  },
);

const mcaPdfReport = computed(() => {
  const pdfReport = unref(formData)?.mcaRecords?.pdf_report_file_url;

  if (!pdfReport) {
    return null;
  }

  return {
    url: pdfReport,
  };
});

const mcaExcelReport = computed(() => {
  const excelReport = unref(formData)?.mcaRecords?.xls_report_file_url;

  if (!excelReport) {
    return null;
  }

  return {
    url: excelReport,
  };
});
const { viewFile, downloadFile } = useFile();
</script>
